import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { PoweredBy } from "../components/PoweredBy";
import { toast } from "sonner";
import {
  FileText,
  Receipt,
  Plus,
  Trash2,
  Lock,
  Search,
  X,
  ArrowRightLeft,
  Link2,
  DollarSign,
  Loader2,
  Calendar,
  TrendingUp,
  AlertCircle,
  Package,
  Wrench,
  Download,
  Mail,
  CalendarRange,
  CalendarClock,
  FileDown,
  CheckCircle2,
  Minus,
  PenLine,
  Pencil,
  Undo2,
  ArrowLeft,
} from "lucide-react";
import { estimatesApi, invoicesApi, customersApi, jobsApi, pricebookApi, documentsApi, membershipPlansApi } from "../lib/api";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import { useAuth } from "../contexts/AuthContext";
import { isLowStock } from "./Pricebook";
import EmailDocModal from "../components/EmailDocModal";
import { applyMembershipToLines, activePlanId, isMemberActive, MEMBERSHIP_TERMS, termLabel } from "../lib/membership";
import { Crown } from "lucide-react";

// Normalize a doc's saved line items for editing — unitPrice becomes the LIST
// price (membership discount is re-applied for preview/save by the server).
const normEditItems = (doc) =>
  (doc.lineItems || []).map((li) => ({
    id: li.id,
    description: li.description,
    qty: li.qty,
    unitPrice: li.listUnitPrice != null ? li.listUnitPrice : li.unitPrice,
    taxable: li.taxable,
    pricebookId: li.pricebookId || null,
    isMembershipFee: !!li.isMembershipFee,
    membershipPlanId: li.membershipPlanId || null,
    membershipTerm: li.membershipTerm || null,
  }));
import EstimateSignDeposit, { EstimatePresentModal } from "../components/EstimateSignDeposit";
import ProcessPaymentModal from "../components/ProcessPaymentModal";
import MobileDocDetail from "./mobile/MobileDocDetail";
import DocNotesTab from "../components/DocNotesTab";

const EST_STATUSES = ["Draft", "Sent", "Accepted", "Declined", "Converted", "Expired"];
const INV_STATUSES = ["Unpaid", "Partially Paid", "Paid", "Overdue", "Voided"];
const PAY_METHODS = ["cash", "check", "ach", "card", "other"];

// Display-only label mapping — the underlying stored status ("Sent") is never
// changed (AI orchestration eligibility, filters-by-value, etc. all keep using
// the raw value). "Sent" simply reads as "Unsold" everywhere it's shown to a
// user; every other status (including "Draft") is unaffected.
export function estimateStatusLabel(status) {
  return status === "Sent" ? "Unsold" : status;
}

const STATUS_PILL = {
  // Estimates
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-blue-100 text-blue-700",
  Accepted: "bg-emerald-100 text-emerald-700",
  Declined: "bg-rose-100 text-rose-700",
  Converted: "bg-[#098058] text-white",
  Expired: "bg-amber-100 text-amber-700",
  // Invoices
  Unpaid: "bg-slate-100 text-slate-700",
  "Partially Paid": "bg-amber-100 text-amber-700",
  Paid: "bg-emerald-100 text-emerald-700",
  Overdue: "bg-rose-100 text-rose-700",
  Voided: "bg-slate-200 text-slate-500 line-through",
};

// Payment/deposit status derived from the doc — reflects whether the customer
// has actually paid. `paid` (fully covered), `partial` (some collected but not
// all), or `unpaid` (nothing collected yet). For estimates we use depositPaid
// vs total; for invoices we use amountPaid vs total. The tiny fp fudge (0.005)
// keeps rounding drift from marking a $335 = $335 payment "partial".
export function paymentInfo(doc, kind) {
  const total = Number(doc?.total || 0);
  const paid = kind === "invoice"
    ? Number(doc?.amountPaid || 0)
    : Number(doc?.depositPaid || 0);
  const balance = Math.max(0, total - paid);
  let status;
  if (paid <= 0.005) status = "unpaid";
  else if (paid + 0.005 >= total && total > 0) status = "paid";
  else status = "partial";
  return { paid, balance, status };
}

// Renders the correct pill(s) for a given doc:
//   • paid  → primary green "Paid" pill; if the underlying status is also
//     meaningful (Converted, Accepted, Sent) show it as a small grey sub-chip
//     alongside so you can still tell it was e.g. converted.
//   • partial → primary amber "Partial" pill + the same sub-chip pattern.
//   • unpaid → the existing status pill only.
export function DocStatusPills({ doc, kind, size = "sm" }) {
  const { status: pay } = paymentInfo(doc, kind);
  const s = doc?.status;
  const primaryCls = size === "md" ? "text-[13px] md:text-[12px] px-3 py-1" : "text-[10.5px] px-2 py-0.5";
  const secondaryCls = size === "md" ? "text-[10.5px] px-2 py-0.5" : "text-[10px] px-1.5 py-0.5";
  if (pay === "paid") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`font-bold rounded-full ${primaryCls} bg-emerald-100 text-emerald-700`} data-testid={`doc-pill-paid-${doc?.id}`}>Paid</span>
        {s && s !== "Draft" && (
          <span className={`font-semibold rounded-full ${secondaryCls} ${STATUS_PILL[s] || "bg-slate-100 text-slate-600"}`}>{estimateStatusLabel(s)}</span>
        )}
      </span>
    );
  }
  if (pay === "partial") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`font-bold rounded-full ${primaryCls} bg-amber-100 text-amber-700`} data-testid={`doc-pill-partial-${doc?.id}`}>Partial</span>
        {s && s !== "Draft" && (
          <span className={`font-semibold rounded-full ${secondaryCls} ${STATUS_PILL[s] || "bg-slate-100 text-slate-600"}`}>{estimateStatusLabel(s)}</span>
        )}
      </span>
    );
  }
  return (
    <span className={`font-bold rounded-full ${primaryCls} ${STATUS_PILL[s] || "bg-slate-100 text-slate-700"}`}>
      {estimateStatusLabel(s)}
    </span>
  );
}

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export default function Estimates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("estimates");
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(null); // { kind: 'estimate'|'invoice', doc }
  const [creating, setCreating] = useState(false);
  const [profileCustomerId, setProfileCustomerId] = useState(null);

  // ---- Date-range filter (URL-persisted) ----
  // `dateField`: "issued" → filter on `issuedAt`.
  //              "due"    → estimates use `expiresAt`, invoices use `dueAt`.
  const fromDate = searchParams.get("from") || "";
  const toDate = searchParams.get("to") || "";
  const dateField = searchParams.get("dateField") === "due" ? "due" : "issued";

  const updateDateParam = (next) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setSearchParams(p, { replace: true });
  };

  const setFromDate = (v) => updateDateParam({ from: v });
  const setToDate = (v) => updateDateParam({ to: v });
  const setDateField = (v) => updateDateParam({ dateField: v === "issued" ? "" : v });
  const clearDates = () => updateDateParam({ from: "", to: "" });

  const applyPreset = (preset) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    let f, t;
    if (preset === "this-month") {
      f = new Date(today.getFullYear(), today.getMonth(), 1);
      t = today;
    } else if (preset === "last-30") {
      f = new Date(today);
      f.setDate(f.getDate() - 29);
      t = today;
    } else if (preset === "this-quarter") {
      const q = Math.floor(today.getMonth() / 3);
      f = new Date(today.getFullYear(), q * 3, 1);
      t = today;
    } else if (preset === "ytd") {
      f = new Date(today.getFullYear(), 0, 1);
      t = today;
    } else {
      return;
    }
    updateDateParam({ from: fmt(f), to: fmt(t) });
  };

  // Escape a single CSV cell — wrap in quotes if it contains comma/quote/newline.
  const csvCell = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const exportCsv = () => {
    const isInvoices = tab === "invoices";
    const headers = isInvoices
      ? ["ID", "Customer", "Title", "Issued", "Due", "Total", "Amount paid", "Balance due", "Status"]
      : ["ID", "Customer", "Title", "Issued", "Expires", "Total", "Status"];
    const fmtDate = (v) => {
      if (!v) return "";
      const d = new Date(v.length === 10 ? `${v}T00:00:00` : v);
      if (Number.isNaN(d.getTime())) return v;
      return d.toISOString().slice(0, 10);
    };
    const rows = filtered.map((d) =>
      isInvoices
        ? [d.id, d.customer, d.title || "", fmtDate(d.issuedAt), fmtDate(d.dueAt), d.total ?? 0, d.amountPaid ?? 0, d.balanceDue ?? 0, d.status]
        : [d.id, d.customer, d.title || "", fmtDate(d.issuedAt), fmtDate(d.expiresAt), d.total ?? 0, d.status],
    );
    const body = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
    // BOM so Excel opens UTF-8 correctly.
    const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
    a.href = url;
    a.download = `${isInvoices ? "invoices" : "estimates"}${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success(`Exported ${rows.length} ${isInvoices ? "invoice" : "estimate"}${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [e, i, c, j] = await Promise.all([
        estimatesApi.list(),
        invoicesApi.list(),
        customersApi.list(),
        jobsApi.list(),
      ]);
      setEstimates(e);
      setInvoices(i);
      setCustomers(c);
      setJobs(j);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Deep-link: /estimates?id=EST-1042 (or INV-2001) auto-opens that doc's drawer.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || loading) return;
    if (selected?.doc?.id === id) return;
    const est = estimates.find((e) => e.id === id);
    if (est) {
      setTab("estimates");
      setSelected({ kind: "estimate", doc: est });
      return;
    }
    const inv = invoices.find((i) => i.id === id);
    if (inv) {
      setTab("invoices");
      setSelected({ kind: "invoice", doc: inv });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, estimates, invoices]);

  // ---- KPI strip (top 3 numbers) ----
  const kpis = useMemo(() => {
    const outstanding = invoices
      .filter((i) => i.status !== "Voided")
      .reduce((s, i) => s + (Number(i.balanceDue) || 0), 0);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const paidThisMonth = invoices.reduce((sum, inv) => {
      return (
        sum +
        (inv.payments || []).reduce((s, p) => {
          const d = new Date(p.receivedAt);
          return d >= monthStart ? s + (Number(p.amount) || 0) : s;
        }, 0)
      );
    }, 0);
    const openEstimates = estimates.filter((e) =>
      ["Draft", "Sent", "Accepted"].includes(e.status),
    ).length;
    return { outstanding, paidThisMonth, openEstimates };
  }, [invoices, estimates]);

  // ---- filtered list ----
  const filtered = useMemo(() => {
    const src = tab === "estimates" ? estimates : invoices;
    const q = search.trim().toLowerCase();
    // Inclusive boundaries: from-00:00:00 UTC → to-23:59:59.999 UTC.
    const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
    const pickDate = (d) => {
      if (dateField === "due") {
        return tab === "estimates" ? d.expiresAt : d.dueAt;
      }
      return d.issuedAt;
    };
    return src.filter((d) => {
      if (statusFilter !== "All" && d.status !== statusFilter) return false;
      if (fromMs !== null || toMs !== null) {
        const v = pickDate(d);
        if (!v) return false;
        const ts = new Date(v.length === 10 ? `${v}T00:00:00` : v).getTime();
        if (Number.isNaN(ts)) return false;
        if (fromMs !== null && ts < fromMs) return false;
        if (toMs !== null && ts > toMs) return false;
      }
      if (!q) return true;
      const hay = `${d.id} ${d.customer} ${d.title || ""} ${estimateStatusLabel(d.status)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [tab, estimates, invoices, search, statusFilter, fromDate, toDate, dateField]);

  const statusOptions = tab === "estimates" ? EST_STATUSES : INV_STATUSES;

  const openCreate = () => {
    setCreating(true);
  };

  const refreshAfter = async (updated, kind) => {
    if (kind === "estimate") {
      setEstimates((prev) => {
        const exists = prev.some((e) => e.id === updated.id);
        return exists ? prev.map((e) => (e.id === updated.id ? updated : e)) : [updated, ...prev];
      });
    } else {
      setInvoices((prev) => {
        const exists = prev.some((i) => i.id === updated.id);
        return exists ? prev.map((i) => (i.id === updated.id ? updated : i)) : [updated, ...prev];
      });
    }
    setSelected({ kind, doc: updated });
  };

  usePageHeader({ title: "Estimates & Invoices", subtitle: `${estimates.length} estimates · ${invoices.length} invoices` });

  return (
    <>
      <div className="px-6 py-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-4">
          <Kpi
            icon={DollarSign}
            label="Outstanding"
            value={money(kpis.outstanding)}
            accent="text-amber-700 bg-amber-50"
            testid="kpi-outstanding"
          />
          <Kpi
            icon={TrendingUp}
            label="Paid this month"
            value={money(kpis.paidThisMonth)}
            accent="text-emerald-700 bg-emerald-50"
            testid="kpi-paid-month"
          />
          <Kpi
            icon={FileText}
            label="Open estimates"
            value={kpis.openEstimates}
            accent="text-blue-700 bg-blue-50"
            testid="kpi-open-estimates"
          />
        </div>

        {/* Tabs + toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
            <TabButton active={tab === "estimates"} onClick={() => { setTab("estimates"); setStatusFilter("All"); }} icon={FileText} label="Estimates" count={estimates.length} testid="tab-estimates" />
            <TabButton active={tab === "invoices"} onClick={() => { setTab("invoices"); setStatusFilter("All"); }} icon={Receipt} label="Invoices" count={invoices.length} testid="tab-invoices" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${tab}…`}
                data-testid="billing-search"
                className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-56"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="billing-status-filter"
              className="h-9 px-2.5 rounded-lg border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            >
              <option value="All">All statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{estimateStatusLabel(s)}</option>
              ))}
            </select>
            <Button
              onClick={openCreate}
              data-testid="billing-new-btn"
              className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New {tab === "estimates" ? "Estimate" : "Invoice"}
            </Button>
          </div>
        </div>

        {/* Date-range filter row */}
        <div className="bg-white rounded-xl border border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="date-range-bar">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 pr-1.5 border-r border-slate-200 mr-1">
            <CalendarRange className="h-3.5 w-3.5 text-blue-600" /> Date range
          </div>
          {/* By: Issued / Due toggle */}
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5" data-testid="date-field-toggle">
            <button
              onClick={() => setDateField("issued")}
              data-testid="date-field-issued"
              className={`h-7 px-2 rounded text-[11.5px] font-semibold transition-colors ${
                dateField === "issued" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Issued
            </button>
            <button
              onClick={() => setDateField("due")}
              data-testid="date-field-due"
              title={tab === "estimates" ? "Filter by Expires date" : "Filter by Due date"}
              className={`h-7 px-2 rounded text-[11.5px] font-semibold transition-colors ${
                dateField === "due" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {tab === "estimates" ? "Expires" : "Due"}
            </button>
          </div>
          {/* From / To */}
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] font-semibold text-slate-500">From</label>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="date-from"
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            <label className="text-[11px] font-semibold text-slate-500">To</label>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="date-to"
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            {(fromDate || toDate) && (
              <button
                onClick={clearDates}
                data-testid="date-clear"
                title="Clear date range"
                className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Presets */}
          <div className="flex items-center gap-1 ml-auto">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            {[
              { k: "this-month", l: "This month" },
              { k: "last-30", l: "Last 30 days" },
              { k: "this-quarter", l: "This quarter" },
              { k: "ytd", l: "YTD" },
            ].map((p) => (
              <button
                key={p.k}
                onClick={() => applyPreset(p.k)}
                data-testid={`date-preset-${p.k}`}
                className="h-7 px-2 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700 border border-transparent hover:border-blue-200 transition-colors"
              >
                {p.l}
              </button>
            ))}
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <button
              onClick={exportCsv}
              disabled={filtered.length === 0}
              data-testid="export-csv-btn"
              title={`Download ${filtered.length} filtered ${tab} as CSV`}
              className="h-7 px-2 rounded-md text-[11.5px] font-semibold border transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300"
            >
              <FileDown className="h-3.5 w-3.5" /> Export CSV
              <span className="text-[10px] font-bold text-emerald-600/80">({filtered.length})</span>
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50/60 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                <th className="px-4 py-2.5 w-28">{tab === "estimates" ? "Estimate #" : "Invoice #"}</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5 w-32">Issued</th>
                <th className="px-4 py-2.5 w-32">Total</th>
                {tab === "invoices" && <th className="px-4 py-2.5 w-32">Balance</th>}
                <th className="px-4 py-2.5 w-28">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={tab === "invoices" ? 7 : 6} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={tab === "invoices" ? 7 : 6} className="px-4 py-10 text-center text-slate-400 italic">
                  No {tab} found.
                </td></tr>
              ) : filtered.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setSelected({ kind: tab === "estimates" ? "estimate" : "invoice", doc: d })}
                  data-testid={`billing-row-${d.id}`}
                  className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{d.id}</td>
                  <td className="px-4 py-2.5 text-slate-800">{d.customer}</td>
                  <td className="px-4 py-2.5 text-slate-600 truncate max-w-[280px]">{d.title || <span className="italic text-slate-400">(no title)</span>}</td>
                  <td className="px-4 py-2.5 text-slate-600">{formatStamp(d.issuedAt)}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{money(d.total)}</td>
                  {tab === "invoices" && (
                    <td className="px-4 py-2.5 font-semibold text-amber-700">{money(d.balanceDue)}</td>
                  )}
                  <td className="px-4 py-2.5">
                    <DocStatusPills doc={d} kind={tab === "invoices" ? "invoice" : "estimate"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DocDetail
          kind={selected.kind}
          doc={selected.doc}
          customers={customers}
          jobs={jobs}
          onClose={() => {
            setSelected(null);
            if (searchParams.get("id")) {
              const p = new URLSearchParams(searchParams);
              p.delete("id");
              setSearchParams(p, { replace: true });
            }
          }}
          onUpdated={(updated) => refreshAfter(updated, selected.kind)}
          onOpenCustomer={(customerId) => setProfileCustomerId(customerId)}
          onDeleted={(id) => {
            if (selected.kind === "estimate") {
              setEstimates((prev) => prev.filter((e) => e.id !== id));
            } else {
              setInvoices((prev) => prev.filter((i) => i.id !== id));
            }
            setSelected(null);
          }}
          onConverted={(newInvoice) => {
            setInvoices((prev) => [newInvoice, ...prev]);
            // Refresh the source estimate
            estimatesApi.get(selected.doc.id).then((e) => {
              setEstimates((prev) => prev.map((x) => (x.id === e.id ? e : x)));
              setSelected({ kind: "invoice", doc: newInvoice });
            });
            toast.success(`Invoice ${newInvoice.id} created from ${selected.doc.id}`);
          }}
        />
      )}
      {creating && (
        <CreateDialog
          kind={tab === "estimates" ? "estimate" : "invoice"}
          customers={customers}
          jobs={jobs}
          onClose={() => setCreating(false)}
          onCreated={(doc) => {
            setCreating(false);
            if (tab === "estimates") {
              setEstimates((prev) => [doc, ...prev]);
            } else {
              setInvoices((prev) => [doc, ...prev]);
            }
            setSelected({ kind: tab === "estimates" ? "estimate" : "invoice", doc });
          }}
        />
      )}
      <CustomerProfileOverlay customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} />
    </>
  );
}

function TabButton({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
        active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>
        {count}
      </span>
    </button>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className="text-[20px] font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

// ---------- Create dialog ----------
export function CreateDialog({ kind, customers, jobs, onClose, onCreated, prefillCustomerId = "" }) {
  const [customerId, setCustomerId] = useState(prefillCustomerId);
  const [jobId, setJobId] = useState("");
  const [title, setTitle] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const jobOptions = useMemo(() => {
    if (!customerId) return [];
    return jobs.filter((j) => j.customerId === customerId);
  }, [jobs, customerId]);

  const submit = async () => {
    if (!customerId) { setError("Customer is required"); return; }
    setError("");
    setSubmitting(true);
    try {
      const api = kind === "estimate" ? estimatesApi : invoicesApi;
      const created = await api.create({
        customerId,
        jobId: jobId || null,
        title: title.trim(),
        taxRate: Number(taxRate) || 0,
        lineItems: [],
      });
      toast.success(`${kind === "estimate" ? "Estimate" : "Invoice"} ${created.id} created`);
      onCreated(created);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 my-auto flex flex-col max-h-[calc(100dvh-2rem)]"
        data-testid="create-dialog"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="text-[14px] font-semibold text-slate-900">
            New {kind === "estimate" ? "Estimate" : "Invoice"}
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          <Field label="Customer">
            <select
              value={customerId}
              onChange={(e) => { setCustomerId(e.target.value); setJobId(""); }}
              data-testid="create-customer-select"
              className={inputClass}
            >
              <option value="">Select customer…</option>
              {customers.map((c) => {
                const label = `${c.name} (${c.type})`;
                return <option key={c.id} value={c.id}>{label}</option>;
              })}
            </select>
          </Field>
          <Field label="Link to Job (optional)">
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              disabled={!customerId}
              data-testid="create-job-select"
              className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
            >
              <option value="">{customerId ? "None" : "Pick a customer first"}</option>
              {jobOptions.map((j) => {
                const label = `${j.id} · ${j.type}${j.start ? ` · ${formatStamp(j.start)}` : ""}`;
                return <option key={j.id} value={j.id}>{label}</option>;
              })}
            </select>
          </Field>
          <Field label="Title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., HVAC Tune-up Quote"
              data-testid="create-title"
              className={inputClass}
            />
          </Field>
          <Field label="Tax rate (%)">
            <input
              type="number"
              min={0}
              step={0.1}
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              data-testid="create-tax"
              className={inputClass}
            />
          </Field>
          {error && <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40 shrink-0">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !customerId}
            data-testid="create-submit"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : <><Plus className="h-4 w-4 mr-1.5" /> Create</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Detail drawer ----------
export function DocDetail({ kind, doc, customers, jobs, onClose, onUpdated, onDeleted, onConverted, onOpenCustomer, onCloseJob, mobileBackLabel, mobileLayout }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEstimate = kind === "estimate";
  const api = isEstimate ? estimatesApi : invoicesApi;
  const [draft, setDraft] = useState({
    title: doc.title || "",
    notes: doc.notes || "",
    jobSummary: doc.jobSummary || "",
    taxRate: doc.taxRate || 0,
    discount: doc.discount || 0,
    expiresAt: doc.expiresAt || "",
    dueAt: doc.dueAt || "",
    jobId: doc.jobId || "",
    membershipPlanId: doc.membershipPlanId || null,
  });
  const [items, setItems] = useState(() => normEditItems(doc));
  const [plans, setPlans] = useState([]);
  const [pbMap, setPbMap] = useState({});
  const [sellOpen, setSellOpen] = useState(false); // retained: unused after pricebook membership move
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentDoc, setPresentDoc] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Bookkeeping lock: once an estimate is converted to an invoice it freezes.
  const locked = isEstimate && (doc.status === "Converted" || !!doc.convertedInvoiceId);

  // View vs edit: Drafts open straight into the editable form; every other
  // status opens as a read-only "document" (ServiceTitan style). An Edit button
  // flips an active (non-frozen) doc back into the form.
  const [editMode, setEditMode] = useState(false);
  const isDraft = doc.status === "Draft";
  const editing = isDraft || editMode;
  // Frozen history records — only PDF + Email actions.
  const isHistory = isEstimate
    ? (locked || ["Declined", "Expired"].includes(doc.status))
    : (["Paid", "Voided"].includes(doc.status) || (Number(doc.balanceDue) <= 0 && Number(doc.amountPaid) > 0));
  const canEdit = !locked && !isHistory && !["Paid", "Converted"].includes(doc.status);

  const customer = useMemo(
    () => customers.find((c) => c.id === doc.customerId) || null,
    [customers, doc.customerId],
  );

  // ----- Membership pricing -----
  useEffect(() => {
    membershipPlansApi.list().then((p) => setPlans(p.filter((x) => x.active))).catch(() => {});
    pricebookApi.list().then((rows) => {
      const m = {};
      rows.forEach((r) => {
        m[r.id] = {
          memberExempt: r.memberExempt, memberPrice: r.memberPrice,
          name: r.name, description: r.description, sku: r.sku, unit: r.unit, kind: r.kind,
        };
      });
      setPbMap(m);
    }).catch(() => {});
  }, []);

  // Auto-surface the "Start the job now?" prompt once after acceptance/conversion.


  const sellingFeeLine = useMemo(() => items.find((li) => li.isMembershipFee && li.membershipPlanId), [items]);
  const effectivePlanId = draft.membershipPlanId || sellingFeeLine?.membershipPlanId || activePlanId(customer);
  const effectivePlan = useMemo(
    () => plans.find((p) => p.id === effectivePlanId) || null,
    [plans, effectivePlanId],
  );
  const priced = useMemo(
    () => applyMembershipToLines(items, effectivePlan, pbMap),
    [items, effectivePlan, pbMap],
  );
  const pricedById = useMemo(() => {
    const m = {};
    priced.lines.forEach((l) => { m[l.id] = l; });
    return m;
  }, [priced]);
  const customerIsMember = isMemberActive(customer);

  const downloadPdf = async () => {
    if (dirty) {
      const ok = window.confirm("You have unsaved changes. The PDF will reflect the last saved state. Continue?");
      if (!ok) return;
    }
    setDownloading(true);
    try {
      const fn = isEstimate ? documentsApi.downloadEstimatePdf : documentsApi.downloadInvoicePdf;
      await fn(doc.id);
      toast.success(`${doc.id}.pdf downloaded`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const openEmail = () => {
    if (dirty) {
      const ok = window.confirm("You have unsaved changes. The emailed PDF will reflect the last saved state. Continue?");
      if (!ok) return;
    }
    setEmailOpen(true);
  };

  const payLink = !isEstimate ? `${window.location.origin}/pay/${doc.id}` : null;
  const copyPayLink = async () => {
    try {
      await navigator.clipboard.writeText(payLink);
      toast.success("Pay link copied — share it with the customer");
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const acceptLink = isEstimate ? `${window.location.origin}/accept/${doc.id}` : null;
  const copyAcceptLink = async () => {
    try {
      await navigator.clipboard.writeText(acceptLink);
      toast.success("Accept link copied — the customer can review, sign & pay a deposit");
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const handleEmailSent = async () => {
    // Re-fetch the doc so the new status/lastSentAt reflects in the UI.
    try {
      const fresh = await api.get(doc.id);
      onUpdated(fresh);
    } catch (err) {
      console.error(err);
    }
  };

  // Re-sync if a different doc selected
  useEffect(() => {
    setDraft({
      title: doc.title || "",
      notes: doc.notes || "",
      jobSummary: doc.jobSummary || "",
      taxRate: doc.taxRate || 0,
      discount: doc.discount || 0,
      expiresAt: doc.expiresAt || "",
      dueAt: doc.dueAt || "",
      jobId: doc.jobId || "",
      membershipPlanId: doc.membershipPlanId || null,
    });
    setItems(normEditItems(doc));
    setDirty(false);
    setEditMode(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  const mark = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };

  const totals = useMemo(() => {
    const ml = priced.lines;
    const subtotal = ml.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.memberUnitPrice) || 0), 0);
    const listSubtotal = ml.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.listUnitPrice) || 0), 0);
    const taxableSub = ml.reduce((s, li) => (li.taxable !== false ? s + (Number(li.qty) || 0) * (Number(li.memberUnitPrice) || 0) : s), 0);
    const discount = Math.max(0, Number(draft.discount) || 0);
    const taxableAfterDisc = subtotal > 0 ? Math.max(0, taxableSub - discount * (taxableSub / subtotal)) : 0;
    const tax = taxableAfterDisc * ((Number(draft.taxRate) || 0) / 100);
    const total = Math.max(0, subtotal - discount + tax);
    return { subtotal, tax, total, savings: priced.savings, listSubtotal };
  }, [priced, draft.taxRate, draft.discount]);

  const addLine = () => {
    setItems((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}`, description: "", qty: 1, unitPrice: 0, taxable: true },
    ]);
    setDirty(true);
  };

  // Sell a membership: add a (non-taxable) fee line + apply that tier's discount.
  const addMembershipLine = (plan, term) => {
    const price = Number(plan.termPrices?.[term]) || 0;
    setItems((prev) => [
      ...prev.filter((li) => !li.isMembershipFee),
      {
        id: `tmp-${Date.now()}`,
        description: `${plan.name} Membership (${termLabel(term)})`,
        qty: 1,
        unitPrice: price,
        taxable: false,
        isMembershipFee: true,
        membershipPlanId: plan.id,
        membershipTerm: term,
      },
    ]);
    setDraft((d) => ({ ...d, membershipPlanId: plan.id }));
    setDirty(true);
    toast.success(`${plan.name} membership added — ${plan.discountPct}% applied to this ${kind}`);
  };

  const updateLine = (id, patch) => {
    setItems((prev) => prev.map((li) => (li.id === id ? { ...li, ...patch } : li)));
    setDirty(true);
  };

  const removeLine = (id) => {
    setItems((prev) => prev.filter((li) => li.id !== id));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: draft.title,
        notes: draft.notes,
        jobSummary: draft.jobSummary,
        taxRate: Number(draft.taxRate) || 0,
        discount: Number(draft.discount) || 0,
        membershipPlanId: effectivePlanId || null,
        lineItems: items.map((li) => ({
          ...(li.id && !String(li.id).startsWith("tmp-") ? { id: li.id } : {}),
          description: li.description,
          qty: Number(li.qty) || 0,
          unitPrice: Number(li.unitPrice) || 0,
          taxable: li.taxable !== false,
          pricebookId: li.pricebookId || null,
          isMembershipFee: !!li.isMembershipFee,
          membershipPlanId: li.membershipPlanId || null,
          membershipTerm: li.membershipTerm || null,
        })),
        jobId: draft.jobId || null,
      };
      if (isEstimate) payload.expiresAt = draft.expiresAt || null;
      else payload.dueAt = draft.dueAt || null;
      const updated = await api.patch(doc.id, payload);
      onUpdated(updated);
      setDirty(false);
      toast.success("Saved");
      return updated;
    } catch (err) {
      toast.error("Save failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const openPresent = async () => {
    let current = doc;
    if (dirty) {
      const updated = await save();
      if (!updated) return;
      current = updated;
    }
    setPresentDoc(current);
    setPresentOpen(true);
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const updated = await api.setStatus(doc.id, status);
      onUpdated(updated);
      toast.success(`Status → ${status}`);
    } catch (err) {
      toast.error("Status change failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const inv = await estimatesApi.convertToInvoice(doc.id);
      onConverted(inv);
    } catch (err) {
      toast.error("Convert failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const viewInvoice = async () => {
    if (!doc.convertedInvoiceId) return;
    setBusy(true);
    try {
      const inv = await invoicesApi.get(doc.convertedInvoiceId);
      onConverted(inv);
    } catch (err) {
      toast.error("Could not open the invoice");
    } finally {
      setBusy(false);
    }
  };

  // Save Changes click: persist edits (if any).
  const onSaveClick = async () => {
    if (dirty) await save();
  };

  const del = async () => {
    if (!window.confirm(`Delete ${doc.id}? This cannot be undone.`)) return;
    if (user?.role !== "admin") {
      toast.error("Only admins can delete");
      return;
    }
    setBusy(true);
    try {
      await api.remove(doc.id);
      toast.success(`${doc.id} deleted`);
      onDeleted(doc.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const jobOptions = useMemo(
    () => jobs.filter((j) => j.customerId === doc.customerId),
    [jobs, doc.customerId],
  );
  const linkedJob = jobs.find((j) => j.id === draft.jobId) || null;

  return (
    <div className="fixed inset-0 z-[55] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      {mobileLayout ? (
        <MobileDocDetail
          kind={kind} doc={doc} isEstimate={isEstimate} customer={customer} linkedJob={linkedJob}
          draft={draft} mark={mark} items={items} pricedById={pricedById} totals={totals} effectivePlan={effectivePlan}
          editing={editing} locked={locked} isHistory={isHistory} canEdit={canEdit} isDraft={isDraft}
          busy={busy} dirty={dirty} downloading={downloading}
          addLine={addLine} updateLine={updateLine} removeLine={removeLine} openPicker={() => setPickerOpen(true)}
          onSaveClick={onSaveClick} openPresent={openPresent} openEmail={openEmail} convert={convert} viewInvoice={viewInvoice}
          downloadPdf={downloadPdf} del={del} copyPayLink={copyPayLink} copyAcceptLink={copyAcceptLink} payLink={payLink} acceptLink={acceptLink}
          onClose={onClose} onOpenCustomer={onOpenCustomer} onOpenJob={(jobId) => jobId && navigate(`/mobile/jobs/${jobId}`)} user={user}
          mobileBackLabel={mobileBackLabel}
        />
      ) : (
      <div onClick={(e) => e.stopPropagation()} className="relative w-full md:w-[720px] md:max-w-[95vw] h-full bg-white shadow-2xl border-l border-slate-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-white gap-2">
          {mobileBackLabel && (
            <button
              onClick={onClose}
              data-testid="doc-detail-mobile-back"
              className="md:hidden flex items-center gap-1 h-9 pl-1 pr-2 -ml-1 rounded-lg text-[#2563EB] font-bold text-[13px] hover:bg-slate-50 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> {mobileBackLabel}
            </button>
          )}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isEstimate ? "bg-blue-600" : "bg-emerald-600"} text-white`}>
              {isEstimate ? <FileText className="h-5 w-5" /> : <Receipt className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {isEstimate ? "Estimate" : "Invoice"} ·{" "}
                {onOpenCustomer && doc.customerId ? (
                  <button
                    type="button"
                    onClick={() => onOpenCustomer(doc.customerId)}
                    data-testid="doc-customer-link"
                    className="text-blue-600 hover:underline font-semibold uppercase"
                    title={`Open ${doc.customer}'s profile`}
                  >
                    {doc.customer}
                  </button>
                ) : (
                  doc.customer
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[18px] font-bold text-slate-900">{doc.id}</div>
                <DocStatusPills doc={doc} kind={kind} />
                {isEstimate && doc.convertedInvoiceId && (
                  <span className="text-[10.5px] font-semibold text-violet-700">→ {doc.convertedInvoiceId}</span>
                )}
                {!isEstimate && doc.estimateId && (
                  <span className="text-[10.5px] font-semibold text-blue-700">from {doc.estimateId}</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="detail-close" aria-label="Close" className="h-8 w-8 flex items-center justify-center shrink-0">
            <X className="h-5 w-5" style={{ color: "#ffffff" }} strokeWidth={2.5} />
          </button>
        </div>

        {/* Contextual status date — directly below the EST number, above Title */}
        {isEstimate && (doc.status === "Sent" || doc.status === "Draft") && (
          <div className="px-5 pt-2.5 pb-0.5 bg-white" data-testid="doc-detail-status-date">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
              {doc.status === "Sent" ? "Estimate Sent Date" : "Draft Date"}
            </span>{" "}
            <span className="text-[12.5px] font-semibold text-slate-700">
              {fmtDocDate(doc.status === "Sent" ? doc.lastSentAt : doc.issuedAt)}
            </span>
          </div>
        )}

        {/* Mobile action icons — top-left (Download / Email / Delete) */}
        {editing ? (
        <>
        <div className="md:hidden px-4 py-2.5 border-b border-slate-100 bg-white flex items-center gap-2.5">
          <button
            onClick={downloadPdf}
            disabled={downloading || busy}
            data-testid="detail-download-pdf-mobile"
            aria-label="Download PDF"
            className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
          </button>
          <button
            onClick={openEmail}
            disabled={busy}
            data-testid="detail-email-customer-mobile"
            aria-label="Email to customer"
            className="h-11 w-11 rounded-lg bg-[#2563EB] text-white flex items-center justify-center hover:bg-[#1D4ED8] disabled:opacity-50"
          >
            <Mail className="h-5 w-5" />
          </button>
          {user?.role === "admin" && (
            <button
              onClick={del}
              disabled={busy}
              data-testid="detail-delete-mobile"
              aria-label="Delete"
              className="h-11 w-11 rounded-lg border border-rose-200 text-rose-600 flex items-center justify-center hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Top fields */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title">
              <input
                value={draft.title}
                onChange={(e) => mark({ title: e.target.value })}
                placeholder="e.g., Q3 HVAC Maintenance Quote"
                data-testid="detail-title"
                disabled={locked}
                className={inputClass}
              />
            </Field>
            <Field label="Linked Job">
              <select
                value={draft.jobId}
                onChange={(e) => mark({ jobId: e.target.value })}
                data-testid="detail-jobid"
                disabled={locked}
                className={inputClass}
              >
                <option value="">None</option>
                {jobOptions.map((j) => {
                  const label = `${j.id} · ${j.type}`;
                  return <option key={j.id} value={j.id}>{label}</option>;
                })}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Tax rate (%)">
              <input
                type="number"
                min={0}
                step={0.1}
                value={draft.taxRate}
                onChange={(e) => mark({ taxRate: e.target.value })}
                data-testid="detail-tax"
                disabled={locked}
                className={inputClass}
              />
            </Field>
            <Field label="Discount ($)">
              <input
                type="number"
                min={0}
                step={0.01}
                value={draft.discount}
                onChange={(e) => mark({ discount: e.target.value })}
                data-testid="detail-discount"
                disabled={locked}
                className={inputClass}
              />
            </Field>
            {isEstimate ? (
              <Field label="Expires">
                <input
                  type="date"
                  value={draft.expiresAt ? draft.expiresAt.slice(0, 10) : ""}
                  onChange={(e) => mark({ expiresAt: e.target.value })}
                  data-testid="detail-expires"
                  disabled={locked}
                  className={inputClass}
                />
              </Field>
            ) : (
              <Field label="Due date">
                <input
                  type="date"
                  value={draft.dueAt ? draft.dueAt.slice(0, 10) : ""}
                  onChange={(e) => mark({ dueAt: e.target.value })}
                  data-testid="detail-due"
                  className={inputClass}
                />
              </Field>
            )}
          </div>

          {/* Job Summary — Guru's visit summary, printed above the line items */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3" data-testid="detail-job-summary-section">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[12px] md:text-[11px] uppercase tracking-wider font-semibold text-blue-700">Job Summary</div>
              <span className="text-[10px] text-[#60A5FA]">Shown above the items on the PDF &amp; email</span>
            </div>
            <textarea
              value={draft.jobSummary}
              onChange={(e) => mark({ jobSummary: e.target.value })}
              rows={3}
              placeholder="Summary of the visit (auto-filled from Guru when the job checklist is completed)…"
              data-testid="detail-job-summary"
              disabled={locked}
              className="w-full px-2.5 py-2 rounded-md border border-blue-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
            />
          </div>


          {/* Line items */}
          <div className="bg-[#CCCDCE]">
            {effectivePlan && (
              <div data-testid="membership-banner" className="mb-2 rounded-lg border px-3 py-2 flex items-center gap-2.5" style={{ borderColor: `${effectivePlan.color}55`, background: `${effectivePlan.color}12` }}>
                <Crown className="h-4 w-4 shrink-0" style={{ color: effectivePlan.color }} />
                <div className="text-[12.5px] text-slate-700 flex-1 min-w-0">
                  <strong style={{ color: effectivePlan.color }}>{effectivePlan.name}</strong> member discount{" "}
                  <strong>{effectivePlan.discountPct}%</strong> applied
                  {sellingFeeLine ? " (new membership on this " + kind + ")" : customerIsMember ? " (active member)" : ""}.
                  {totals.savings > 0 && <span className="text-emerald-700 font-semibold"> Saves {money(totals.savings)}.</span>}
                </div>
              </div>
            )}
            {locked && (
              <div data-testid="estimate-locked-banner" className="mb-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 flex items-center gap-2.5">
                <Lock className="h-4 w-4 shrink-0 text-violet-600" />
                <div className="text-[12.5px] text-slate-700 flex-1 min-w-0">
                  This estimate was accepted and converted to invoice{" "}
                  <strong className="text-violet-700">{doc.convertedInvoiceId}</strong>. It is locked to keep your books accurate — take payment on the invoice.
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[12px] md:text-[11px] uppercase tracking-wider font-semibold text-[#434A5B]">Line items</div>
            </div>
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full text-[14px] md:text-[12.5px]">
                <thead className="bg-slate-50/60">
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
                    <th className="px-2.5 py-2 bg-[#1F3257]">Description</th>
                    <th className="px-2.5 py-2 w-16 text-right bg-[#1F3257]">Qty</th>
                    <th className="px-2.5 py-2 w-24 text-right bg-[#1F3257]">Unit $</th>
                    <th className="px-2.5 py-2 w-14 text-center bg-[#1F3257]">Tax</th>
                    <th className="px-2.5 py-2 w-24 text-right bg-[#1F3257]">Total</th>
                    <th className="px-2.5 py-2 w-20 bg-[#1F3257]"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={6} className="px-2.5 py-6 text-center text-[12px] text-slate-400 italic">
                      No line items yet — use the buttons below to add one.
                    </td></tr>
                  ) : items.map((li) => {
                    const descReadOnly = locked || !!li.pricebookId || !!li.isMembershipFee;
                    return (
                    <tr key={li.id} className="border-t border-slate-100 align-top">
                      <td className="px-2.5 py-1.5 align-top">
                        {descReadOnly ? (
                          <div data-testid={`line-desc-${li.id}`} className="px-2 py-1 text-[12.5px] text-[#434445] whitespace-normal break-words leading-snug">
                            {li.description || <span className="text-slate-400 italic">—</span>}
                            {li.pricebookId && (
                              <span className="ml-1.5 inline-flex items-center text-[9px] font-semibold uppercase tracking-wide text-[#2563EB] align-middle">
                                <Package className="h-2.5 w-2.5 mr-0.5" />pricebook
                              </span>
                            )}
                          </div>
                        ) : (
                          <textarea
                            value={li.description}
                            onChange={(e) => updateLine(li.id, { description: e.target.value })}
                            placeholder="Describe the service or part"
                            rows={1}
                            data-testid={`line-desc-${li.id}`}
                            className="w-full min-h-[28px] px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none text-[12.5px] resize-y whitespace-normal break-words leading-snug"
                          />
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 align-top">
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={li.qty}
                          disabled={locked}
                          onChange={(e) => updateLine(li.id, { qty: parseFloat(e.target.value) || 0 })}
                          data-testid={`line-qty-${li.id}`}
                          className="w-full h-7 px-2 rounded border border-transparent hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none text-[12.5px] text-right disabled:bg-transparent disabled:text-slate-600"
                        />
                      </td>
                      <td className="px-2.5 py-1.5 align-top">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={li.unitPrice}
                          disabled={locked}
                          onChange={(e) => updateLine(li.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                          data-testid={`line-price-${li.id}`}
                          className="w-full h-7 px-2 rounded border border-transparent hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none text-[12.5px] text-right disabled:bg-transparent disabled:text-slate-600"
                        />
                        {pricedById[li.id]?.memberDiscounted && (
                          <div className="text-[10px] text-violet-600 text-right mt-0.5" data-testid={`line-member-price-${li.id}`}>
                            member {money(pricedById[li.id].memberUnitPrice)}
                          </div>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 text-center align-top">
                        <input
                          type="checkbox"
                          checked={li.taxable !== false}
                          disabled={locked}
                          onChange={(e) => updateLine(li.id, { taxable: e.target.checked })}
                          data-testid={`line-taxable-${li.id}`}
                          className="h-3.5 w-3.5 accent-blue-600 mt-1.5"
                        />
                      </td>
                      <td className="px-2.5 py-1.5 text-right align-top font-semibold text-[#434A5B] whitespace-nowrap">
                        {pricedById[li.id]?.memberDiscounted ? (
                          <div>
                            <span className="line-through text-slate-400 text-[11px] mr-1">
                              {money((Number(li.qty) || 0) * (Number(li.unitPrice) || 0))}
                            </span>
                            <span className="text-violet-700">
                              {money((Number(li.qty) || 0) * (Number(pricedById[li.id].memberUnitPrice) || 0))}
                            </span>
                          </div>
                        ) : (
                          money((Number(li.qty) || 0) * (Number(li.unitPrice) || 0))
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 align-top text-right">
                        {!locked && (
                          <button
                            onClick={() => removeLine(li.id)}
                            data-testid={`line-remove-${li.id}`}
                            aria-label="Delete line item"
                            title="Delete"
                            className="inline-flex items-center justify-center h-7 w-7 text-[#C11227] focus:outline-none"
                          >
                            <Trash2 className="h-3.5 w-3.5 shrink-0" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
            {!locked && (
              <div className="mt-3 flex flex-col items-center gap-2 max-w-sm mx-auto">
                <Button
                  onClick={() => setPickerOpen(true)}
                  data-testid="detail-pick-from-pricebook"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-semibold"
                >
                  <Package className="h-4 w-4 mr-1.5" /> Pick from Pricebook
                </Button>
                <Button
                  variant="outline"
                  onClick={addLine}
                  data-testid="detail-add-line"
                  className="w-full h-11 bg-[#2563EB] text-[#FFFFFF] border-[#2563EB] hover:bg-[#2563EB]/90 hover:text-white text-[14px] font-semibold"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Add line
                </Button>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="rounded-md bg-[#CCCDCE] border border-slate-200 p-3 space-y-1 text-[15px] md:text-[13px]" data-testid="totals-block">
            <div className="flex justify-between"><span className="text-[#434A5B]">Subtotal</span><span className="font-semibold text-[#434A5B]">{money(totals.subtotal)}</span></div>
            {totals.savings > 0 && (
              <div className="flex justify-between" data-testid="totals-member-savings"><span className="text-violet-600">Membership savings{effectivePlan ? ` (${effectivePlan.discountPct}%)` : ""}</span><span className="font-semibold text-violet-700">−{money(totals.savings)}</span></div>
            )}
            {Number(draft.discount) > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-semibold text-rose-600">−{money(draft.discount)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[#434A5B]">Tax ({Number(draft.taxRate) || 0}%)</span><span className="font-semibold text-[#434A5B]">{money(totals.tax)}</span></div>
            <div className="flex justify-between pt-1.5 border-t border-slate-200 text-[17px] md:text-[15px]"><span className="font-semibold text-[#434A5B]">Total</span><span className="font-bold text-[#2563EB]">{money(totals.total)}</span></div>
            {(() => {
              // Paid + Balance Remaining rows — shown for invoices always, and
              // for estimates once any deposit has been recorded. Keeps the
              // "money still owed" number one glance away below the Total.
              const info = paymentInfo(doc, kind);
              if (!isEstimate) {
                return (
                  <>
                    <div className="flex justify-between text-[14px] md:text-[12.5px]"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-700">{money(doc.amountPaid)}</span></div>
                    <div className="flex justify-between text-[14px] md:text-[12.5px]"><span className="text-slate-500">Balance Remaining</span><span className="font-bold text-amber-700">{money(doc.balanceDue)}</span></div>
                  </>
                );
              }
              if (info.paid > 0) {
                return (
                  <>
                    <div className="flex justify-between text-[14px] md:text-[12.5px]"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-700">{money(info.paid)}</span></div>
                    <div className="flex justify-between text-[14px] md:text-[12.5px]" data-testid="totals-balance-remaining"><span className="text-slate-500">Balance Remaining</span><span className={`font-bold ${info.balance <= 0.005 ? "text-emerald-700" : "text-amber-700"}`}>{money(info.balance)}</span></div>
                  </>
                );
              }
              return null;
            })()}
          </div>

          {/* Notes */}
          <Field label="Notes">
            <textarea
              rows={3}
              value={draft.notes}
              onChange={(e) => mark({ notes: e.target.value })}
              placeholder="Internal/customer-facing notes…"
              data-testid="detail-notes"
              disabled={locked}
              className={`${inputClass} resize-none py-2 !bg-[#CCCDCE] !text-[#434A5B]`}
            />
          </Field>

          {/* Payments (invoice only) */}
          {!isEstimate && <PaymentsSection invoice={doc} onUpdated={onUpdated} />}

          {/* Signatures + Deposits (estimate only) */}
          {isEstimate && (
            <button
              onClick={openPresent}
              data-testid="detail-present-to-customer"
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
              disabled={busy}
            >
              <PenLine className="h-4 w-4" strokeWidth={2.5} /> Present to customer
            </button>
          )}
          {isEstimate && <EstimateSignDeposit estimate={doc} onUpdated={onUpdated} />}

          {/* Status — read-only; updates automatically as the estimate progresses */}
          <div>
            <div className="text-[12px] md:text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Status</div>
            <div className="flex items-center gap-2" data-testid="doc-status-readonly">
              <DocStatusPills doc={doc} kind={kind} size="md" />
              <span className="text-[11.5px] text-slate-400">Updates automatically</span>
            </div>
          </div>

          {/* Branded footer */}
          <div className="pt-3 mt-1 border-t border-slate-100">
            <PoweredBy />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pt-3 pb-[calc(5rem+1rem+env(safe-area-inset-bottom))] sm:pb-3 border-t border-slate-200 bg-white flex flex-wrap items-center justify-center md:justify-start gap-2">
          <Button
            variant="outline"
            onClick={downloadPdf}
            disabled={downloading || busy}
            data-testid="detail-download-pdf"
            className="hidden md:inline-flex h-9 text-[13px] border-slate-200"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1.5" />
            )}
            Download PDF
          </Button>
          <Button
            onClick={openEmail}
            disabled={busy}
            data-testid="detail-email-customer"
            className="hidden md:inline-flex h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
          >
            <Mail className="h-4 w-4 mr-1.5" /> Email to Customer
          </Button>
          {!isEstimate && doc.balanceDue > 0 && doc.status !== "Voided" && (
            <Button
              variant="outline"
              onClick={copyPayLink}
              disabled={busy}
              data-testid="detail-copy-pay-link"
              className="h-10 md:h-9 text-[14px] md:text-[13px] border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"
            >
              <Link2 className="h-4 w-4 mr-1.5" /> Copy pay link
            </Button>
          )}
          {isEstimate && !["Paid", "Converted"].includes(doc.status) && (
            <Button
              variant="outline"
              onClick={copyAcceptLink}
              disabled={busy}
              data-testid="detail-copy-accept-link"
              className="h-10 md:h-9 text-[14px] md:text-[13px] border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
            >
              <Link2 className="h-4 w-4 mr-1.5" /> Copy accept link
            </Button>
          )}
          {isEstimate && doc.status === "Accepted" && !doc.convertedInvoiceId && (
            <Button
              onClick={convert}
              disabled={busy}
              data-testid="convert-to-invoice"
              className="h-10 md:h-9 bg-violet-600 hover:bg-violet-700 text-white text-[14px] md:text-[13px] font-semibold"
            >
              <ArrowRightLeft className="h-4 w-4 mr-1.5" /> Convert to Invoice
            </Button>
          )}
          {isEstimate && locked && (
            <Button
              onClick={viewInvoice}
              disabled={busy}
              data-testid="view-invoice"
              className="h-10 md:h-9 bg-violet-600 hover:bg-violet-700 text-white text-[14px] md:text-[13px] font-semibold"
            >
              <ArrowRightLeft className="h-4 w-4 mr-1.5" /> View Invoice {doc.convertedInvoiceId}
            </Button>
          )}
          {!locked && (
            <Button
              onClick={onSaveClick}
              disabled={!dirty || busy}
              data-testid="detail-save"
              className="h-10 md:h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[14px] md:text-[13px] font-semibold"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : "Save Changes"}
            </Button>
          )}
          {editMode && !isDraft && (
            <Button
              variant="outline"
              onClick={async () => { if (dirty) { const u = await save(); if (!u) return; } setEditMode(false); }}
              disabled={busy}
              data-testid="detail-done-editing"
              className="h-10 md:h-9 text-[13px] border-slate-300"
            >
              Done
            </Button>
          )}
          <div className="hidden sm:block flex-1" />
          {user?.role === "admin" && (
            <Button variant="outline" onClick={del} disabled={busy} data-testid="detail-delete" className="hidden md:inline-flex h-9 text-[13px] text-rose-600 hover:bg-rose-50 hover:border-rose-300 border-slate-200">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="hidden md:inline-flex h-9 text-[13px]">Close</Button>
        </div>
        </>
        ) : (
          <DocReadOnly
            kind={kind}
            doc={doc}
            customer={customer}
            lines={priced.lines}
            totals={totals}
            pbMap={pbMap}
            effectivePlan={effectivePlan}
            isHistory={isHistory}
            canEdit={canEdit}
            busy={busy}
            downloading={downloading}
            onEdit={() => setEditMode(true)}
            onClose={onClose}
            onDownloadPdf={downloadPdf}
            onEmail={openEmail}
            onPresent={openPresent}
            onConvert={convert}
            onViewInvoice={viewInvoice}
            onCopyPayLink={copyPayLink}
            onCopyAcceptLink={copyAcceptLink}
            onDelete={del}
            onUpdated={onUpdated}
            onCloseJob={onCloseJob}

          />
        )}
      </div>
      )}
      {pickerOpen && (
        <PricebookPicker
          plans={plans}
          onPickMembership={(plan) => { addMembershipLine(plan, "1yr"); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
          onPick={(picks) => {
            const newLines = picks.map(({ item: pbItem, qty }) => ({
              id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              description: pbItem.name,
              qty: qty || 1,
              unitPrice: pbItem.unitPrice,
              taxable: !!pbItem.taxable,
              pricebookId: pbItem.id,
            }));
            setItems((prev) => [...prev, ...newLines]);
            setDirty(true);
            setPickerOpen(false);
            toast.success(`Added ${newLines.length} item${newLines.length === 1 ? "" : "s"} to the estimate`);
          }}
        />
      )}
      {presentOpen && (
        <EstimatePresentModal
          estimate={presentDoc || doc}
          customer={customer}
          plans={plans}
          totals={{
            subtotal: totals.subtotal,
            tax: totals.tax,
            total: totals.total,
            discount: Number(draft.discount) || 0,
            savings: totals.savings,
          }}
          onClose={() => setPresentOpen(false)}
          onUpdated={onUpdated}
          onPaymentOrEmailDone={() => setPresentOpen(false)}
        />
      )}
      {emailOpen && (
        <EmailDocModal
          kind={kind}
          doc={doc}
          customer={customer}
          payLink={payLink}
          acceptLink={acceptLink}
          onClose={() => setEmailOpen(false)}
          onSent={handleEmailSent}
        />
      )}
    </div>
  );
}

function fmtDocDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">{label}</div>
      <div className="text-[13px] font-medium text-slate-800 break-words">{children}</div>
    </div>
  );
}

function ReadItemCard({ li, pb }) {
  const qty = Number(li.qty) || 0;
  const listUnit = Number(li.listUnitPrice != null ? li.listUnitPrice : li.unitPrice) || 0;
  const unit = li.memberDiscounted ? Number(li.memberUnitPrice) || 0 : listUnit;
  const total = qty * unit;
  const code = pb?.sku || pb?.category;
  const desc = pb?.description;
  const kindStr = (pb?.kind || "").toLowerCase();
  const isProduct = kindStr.includes("material") || kindStr.includes("product") || kindStr.includes("part") || kindStr.includes("equip");
  const Icon = li.isMembershipFee ? Crown : (li.pricebookId ? (isProduct ? Package : Wrench) : FileText);
  return (
    <div className="rounded-xl border border-slate-200 p-3.5" data-testid={`ro-item-${li.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[14px] font-semibold text-slate-900 leading-snug">{li.description || "—"}</div>
            <div className="text-[14px] font-bold text-slate-900 whitespace-nowrap">{money(total)}</div>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-slate-500">
            <span>Qty <strong className="text-slate-700">{qty}</strong></span>
            <span className="flex items-center gap-1">
              Price{" "}
              {li.memberDiscounted ? (
                <><span className="line-through text-slate-400">{money(listUnit)}</span><strong className="text-violet-700">{money(unit)}</strong></>
              ) : (
                <strong className="text-slate-700">{money(unit)}</strong>
              )}
            </span>
          </div>
          {code && <div className="mt-1 text-[11px] text-slate-400">Code: <span className="font-medium text-slate-600">{code}</span></div>}
          {desc && <div className="mt-1.5 text-[12.5px] text-slate-500 leading-relaxed">{desc}</div>}
        </div>
      </div>
    </div>
  );
}

function RibbonBar({ testid, barCls, wrapCls, icon: Icon, label, sub }) {
  return (
    <div className={`mx-5 mt-4 mb-1 rounded-xl border ${wrapCls} overflow-hidden`} data-testid={testid}>
      <div className={`px-4 py-2.5 flex items-center gap-2 text-white ${barCls}`}>
        <Icon className="h-4 w-4" />
        <span className="text-[13px] font-extrabold tracking-wide uppercase">{label}</span>
      </div>
      {sub && <div className="px-4 py-2.5 text-[12.5px] font-medium text-slate-700">{sub}</div>}
    </div>
  );
}

// ServiceTitan-style status banner at the top of a read-only doc: SOLD/ACCEPTED
// (with signature + deposit) for estimates, PAID/OVERDUE/PARTIAL for invoices.
function StatusRibbon({ kind, doc }) {
  if (kind === "estimate") {
    const sig = doc.signature;
    const sold = ["Accepted", "Converted"].includes(doc.status) || !!doc.acceptedAt || !!sig;
    if (sold) {
      const who = sig?.signedBy || doc.acceptedBy;
      const when = sig?.signedAt || doc.acceptedAt;
      return (
        <div className="mx-5 mt-4 mb-1 rounded-xl border border-emerald-200 bg-emerald-50 overflow-hidden" data-testid="doc-ribbon-sold">
          <div className="px-4 py-2.5 flex items-center gap-2 bg-emerald-600 text-white">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-[13px] font-extrabold tracking-wide uppercase">{doc.status === "Converted" ? "Sold · Converted" : "Sold · Accepted"}</span>
          </div>
          <div className="px-4 py-3 text-[12.5px] text-emerald-900 space-y-2">
            {who && <div className="text-white">Signed by <strong>{who}</strong>{when ? <> on {fmtDocDate(when)}</> : null}</div>}
            {sig?.dataUrl && (
              <div className="rounded-lg border border-emerald-200 bg-white p-2 inline-block" style={{ colorScheme: "light" }}>
                <img src={sig.dataUrl} alt="Customer signature" className="h-14 max-w-[220px] object-contain bg-white" style={{ colorScheme: "light" }} data-testid="doc-ribbon-signature" />
              </div>
            )}
            {Number(doc.depositPaid) > 0 && <div>Deposit paid: <strong className="text-emerald-700">{money(doc.depositPaid)}</strong></div>}
          </div>
        </div>
      );
    }
    if (doc.status === "Declined") return <RibbonBar testid="doc-ribbon-declined" barCls="bg-rose-600" wrapCls="border-rose-200 bg-rose-50" icon={X} label="Declined" />;
    if (doc.status === "Expired") return <RibbonBar testid="doc-ribbon-expired" barCls="bg-slate-500" wrapCls="border-slate-200 bg-slate-50" icon={AlertCircle} label="Expired" />;
    return null;
  }
  // invoice
  const paid = doc.status === "Paid" || (Number(doc.balanceDue) <= 0 && Number(doc.amountPaid) > 0);
  const voided = doc.status === "Voided";
  const overdue = doc.status === "Overdue" || (doc.dueAt && new Date(doc.dueAt) < new Date() && Number(doc.balanceDue) > 0);
  const partial = Number(doc.amountPaid) > 0 && Number(doc.balanceDue) > 0;
  const payments = doc.payments || [];
  const lastPay = payments.length ? payments[payments.length - 1] : null;
  const paidOn = lastPay ? (lastPay.receivedAt || lastPay.date || lastPay.createdAt) : null;
  if (voided) return <RibbonBar testid="doc-ribbon-voided" barCls="bg-slate-500" wrapCls="border-slate-200 bg-slate-50" icon={Lock} label="Voided" />;
  if (paid) return <RibbonBar testid="doc-ribbon-paid" barCls="bg-emerald-600" wrapCls="border-emerald-200 bg-emerald-50" icon={CheckCircle2} label="Paid" sub={paidOn ? `Paid on ${fmtDocDate(paidOn)}` : null} />;
  if (overdue) return <RibbonBar testid="doc-ribbon-overdue" barCls="bg-rose-600" wrapCls="border-rose-200 bg-rose-50" icon={AlertCircle} label="Overdue" sub={`Balance ${money(doc.balanceDue)}${doc.dueAt ? ` · due ${fmtDocDate(doc.dueAt)}` : ""}`} />;
  if (partial) return <RibbonBar testid="doc-ribbon-partial" barCls="bg-amber-500" wrapCls="border-amber-200 bg-amber-50" icon={AlertCircle} label="Partially Paid" sub={`Balance ${money(doc.balanceDue)} of ${money(doc.total)}`} />;
  return <RibbonBar testid="doc-ribbon-unpaid" barCls="bg-amber-500" wrapCls="border-amber-200 bg-amber-50" icon={AlertCircle} label="Awaiting Payment" sub={`Balance ${money(doc.balanceDue)}${doc.dueAt ? ` · due ${fmtDocDate(doc.dueAt)}` : ""}`} />;
}

// Read-only "document" view for estimates/invoices that have already been made
// (non-Draft). ServiceTitan-style header + tabbed detail. Drafts + explicit Edit
// use the editable form instead.
function DocReadOnly({
  kind, doc, customer, lines, totals, pbMap, isHistory, canEdit, busy, downloading,
  onEdit, onClose, onDownloadPdf, onEmail, onPresent, onConvert, onViewInvoice,
  onCopyPayLink, onCopyAcceptLink, onDelete, onUpdated, onCloseJob,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEstimate = kind === "estimate";
  const payments = doc.payments || [];
  const [tab, setTab] = useState("items");
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [acceptTarget, setAcceptTarget] = useState(null);
  // For converted estimates, cache the linked invoice's outstanding balance so
  // we can hide "Accept Payment" once the follow-up invoice is fully paid.
  // null = unknown/loading, number = current balanceDue.
  const [linkedInvoiceBalance, setLinkedInvoiceBalance] = useState(null);
  const created = doc.issuedAt || doc.createdAt;
  const summary = doc.jobSummary || "";
  const longSummary = summary.length > 220;

  useEffect(() => {
    let cancelled = false;
    if (!isEstimate || !doc.convertedInvoiceId) {
      setLinkedInvoiceBalance(null);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const inv = await invoicesApi.get(doc.convertedInvoiceId);
        if (!cancelled) setLinkedInvoiceBalance(Number(inv?.balanceDue ?? 0));
      } catch (_) {
        if (!cancelled) setLinkedInvoiceBalance(null); // fetch failed → optimistic show
      }
    })();
    return () => { cancelled = true; };
  }, [isEstimate, doc.convertedInvoiceId, doc.id, acceptTarget]);

  // For a converted estimate, load the linked invoice on demand and open the
  // Accept Payment dialog against IT (estimates don't hold payments).
  const openAcceptPayment = async () => {
    if (!doc.convertedInvoiceId) return;
    try {
      const inv = await invoicesApi.get(doc.convertedInvoiceId);
      if ((inv.balanceDue || 0) <= 0) {
        toast.info("This invoice is already paid in full.");
        return;
      }
      setAcceptTarget(inv);
    } catch (err) {
      toast.error("Could not load the linked invoice");
    }
  };

  const canRefund = (p) => {
    if (!isAdmin || isEstimate) return false;
    if ((p.amount || 0) <= 0) return false;
    const method = (p.method || "").toLowerCase();
    if (!["card", "ach"].includes(method)) return false;
    if (!p.ledgerPaymentId) return false;
    const refunded = Number(p.refundedTotal || 0);
    return refunded + 0.005 < Number(p.amount || 0);
  };

  const accentText = isEstimate ? "text-blue-700" : "text-emerald-700";
  const tabActiveCls = isEstimate ? "border-blue-600 text-blue-700" : "border-emerald-600 text-emerald-700";
  const moreCls = isEstimate ? "text-blue-600" : "text-emerald-600";

  const tabs = isEstimate
    ? [{ k: "items", label: `Items ${lines.length}` }, { k: "notes", label: "Notes" }, { k: "job", label: "Job Details" }]
    : [{ k: "items", label: `Items ${lines.length}` }, { k: "notes", label: "Notes" }, { k: "payments", label: `Payments ${payments.length}` }];

  return (
    <>
      <div className="flex-1 overflow-y-auto" data-testid="doc-readonly">
        <StatusRibbon kind={kind} doc={doc} />
        {/* Header summary */}
        <div className="px-5 pt-4 pb-4 border-b border-slate-100">
          {doc.title && <div className="text-[17px] font-bold text-slate-900 mb-2.5" data-testid="doc-readonly-title">{doc.title}</div>}
          <div className="grid grid-cols-2 gap-y-3 gap-x-4">
            <Meta label="Status"><DocStatusPills doc={doc} kind={kind} /></Meta>
            <Meta label={!isEstimate && isHistory ? "Date completed" : "Date created"}>{fmtDocDate(created)}</Meta>
            {doc.createdBy && <Meta label="Created by">{doc.createdBy}</Meta>}
            {doc.jobId && <Meta label="Linked job">{doc.jobId}</Meta>}
          </div>
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">{isEstimate ? "Total price" : "Total"}</span>
            <span className={`text-[22px] font-extrabold ${accentText}`} data-testid="doc-readonly-total">{money(totals.total)}</span>
          </div>
          {(() => {
            // For estimates: show Paid + Balance Remaining rows when any deposit
            // has been collected. Invoices continue to show amountPaid / balanceDue.
            const info = paymentInfo(doc, kind);
            if (info.paid > 0) {
              return (
                <div className="mt-2 flex items-center justify-between text-[13px] px-1" data-testid="doc-readonly-balance">
                  <span className="text-slate-500">Paid <strong className="text-emerald-700">{money(info.paid)}</strong></span>
                  <span className="text-slate-500">Balance Remaining <strong className={info.balance <= 0.005 ? "text-emerald-700" : "text-amber-700"}>{money(info.balance)}</strong></span>
                </div>
              );
            }
            if (!isEstimate) {
              return (
                <div className="mt-2 flex items-center justify-between text-[13px] px-1">
                  <span className="text-slate-500">Paid <strong className="text-emerald-700">{money(doc.amountPaid || 0)}</strong></span>
                  <span className="text-slate-500">Balance Remaining <strong className="text-amber-700">{money(doc.balanceDue || 0)}</strong></span>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Summary text */}
        {summary && (
          <div className="px-5 py-3.5 border-b border-slate-100" data-testid="doc-readonly-summary">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Summary</div>
            <p className="text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">
              {longSummary && !showFullSummary ? summary.slice(0, 220) + "…" : summary}
            </p>
            {longSummary && (
              <button onClick={() => setShowFullSummary((v) => !v)} className={`mt-1 text-[12.5px] font-semibold ${moreCls}`}>{showFullSummary ? "Less" : "More"}</button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 flex px-3">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`doc-readonly-tab-${t.k}`}
              className={`px-3 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? tabActiveCls : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-4">
          {tab === "items" && (
            <div className="space-y-2.5" data-testid="doc-readonly-items">
              {lines.length === 0 ? (
                <div className="text-center text-slate-400 text-[13px] py-8">No line items</div>
              ) : (
                lines.map((li) => <ReadItemCard key={li.id} li={li} pb={pbMap[li.pricebookId]} />)
              )}
              <div className="mt-3 rounded-lg bg-slate-50/70 border border-slate-200 p-3 space-y-1.5 text-[13px]">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{money(totals.listSubtotal != null ? totals.listSubtotal : totals.subtotal)}</span></div>
                {totals.savings > 0 && <div className="flex justify-between"><span className="text-violet-600">Membership savings</span><span className="font-semibold text-violet-700">−{money(totals.savings)}</span></div>}
                {Number(doc.discount) > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-semibold text-rose-600">−{money(doc.discount)}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Tax ({Number(doc.taxRate) || 0}%)</span><span className="font-semibold">{money(totals.tax)}</span></div>
                <div className="flex justify-between pt-1.5 border-t border-slate-200 text-[15px]"><span className="font-semibold">Total</span><span className={`font-bold ${accentText}`}>{money(totals.total)}</span></div>
                {(() => {
                  const info = paymentInfo(doc, kind);
                  if (info.status === "paid") {
                    return (
                      <div className="flex justify-between text-[13px]" data-testid="doc-readonly-totals-balance">
                        <span className="font-semibold text-emerald-700">Balance</span>
                        <span className="font-bold text-emerald-700">{money(0)}</span>
                      </div>
                    );
                  }
                  if (info.status === "partial") {
                    return (
                      <div className="flex justify-between text-[13px]" data-testid="doc-readonly-totals-balance">
                        <span className="font-semibold text-amber-700">Unpaid Balance</span>
                        <span className="font-bold text-amber-700">{money(info.balance)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}
          {tab === "notes" && (
            <DocNotesTab
              doc={doc}
              kind={kind}
              api={isEstimate ? estimatesApi : invoicesApi}
              onUpdated={(updated) => onUpdated?.(updated)}
              currentUserEmail={user?.email}
              isAdmin={isAdmin}
            />
          )}
          {tab === "payments" && (
            <div data-testid="doc-readonly-payments" className="space-y-2">
              {payments.length === 0 ? (
                <div className="text-center text-slate-400 text-[13px] py-6">No payments recorded.</div>
              ) : (
                payments.map((p, i) => {
                  const isRefundRow = (p.amount || 0) < 0 || p.method === "refund";
                  const refundable = canRefund(p);
                  const refunded = Number(p.refundedTotal || 0);
                  return (
                    <div key={p.id || i} className={`flex items-center justify-between rounded-lg border px-3 py-2.5 ${isRefundRow ? "border-rose-200 bg-rose-50/60" : "border-slate-200"}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ${isRefundRow ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {isRefundRow ? <Undo2 className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <div className="text-[13px] font-semibold text-slate-800 capitalize">{p.method || "payment"}</div>
                            {refunded > 0 && !isRefundRow && (
                              <span
                                className="text-[10.5px] font-semibold text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded"
                                data-testid={`payment-refunded-${p.id}`}
                              >
                                {p.status === "refunded" ? "Fully refunded" : `−${money(refunded)} refunded`}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-400">{fmtDocDate(p.receivedAt || p.date || p.createdAt)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[14px] font-bold ${isRefundRow ? "text-rose-700" : "text-emerald-700"}`}>{money(p.amount)}</span>
                        {refundable && (
                          <button
                            onClick={() => setRefundTarget(p)}
                            data-testid={`payment-refund-${p.id}`}
                            className="h-7 px-2 rounded text-[11.5px] font-semibold text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"
                            title="Refund via processor"
                          >
                            <Undo2 className="h-3.5 w-3.5" /> Refund
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 text-[13px]">
                <span className="text-slate-500">Balance due</span><span className="font-bold text-amber-700">{money(doc.balanceDue || 0)}</span>
              </div>
            </div>
          )}
          {tab === "job" && (
            <div data-testid="doc-readonly-job" className="grid grid-cols-2 gap-y-3 gap-x-4">
              <Meta label="Linked job">{doc.jobId || "—"}</Meta>
              <Meta label="Expires">{doc.expiresAt ? fmtDocDate(doc.expiresAt) : "—"}</Meta>
              {doc.acceptedAt && <Meta label="Accepted">{fmtDocDate(doc.acceptedAt)}</Meta>}
              {doc.depositAmount ? <Meta label="Deposit">{money(doc.depositAmount)}</Meta> : null}
              {doc.convertedInvoiceId && <Meta label="Invoice">{doc.convertedInvoiceId}</Meta>}
            </div>
          )}
        </div>

        <div className="px-5 pb-5"><PoweredBy /></div>
      </div>

      {/* Action bar */}
      <div className="px-5 pt-3 pb-24 md:pb-3 border-t border-slate-200 bg-white flex flex-col gap-2.5 md:flex-row md:flex-wrap md:items-center md:gap-2">
        <Button onClick={onEmail} disabled={busy} data-testid="detail-email-customer" className="w-full md:w-auto h-12 md:h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] md:text-[13px] font-semibold">
          <Mail className="h-4 w-4 mr-1.5" /> Email to Customer
        </Button>
        <Button variant="outline" onClick={onDownloadPdf} disabled={downloading || busy} data-testid="detail-download-pdf" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] border-slate-200">
          {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Download PDF
        </Button>
        {!isHistory && (
          <>
            {canEdit && (
              <Button variant="outline" onClick={onEdit} data-testid="detail-edit" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] border-slate-300">
                <Pencil className="h-4 w-4 mr-1.5" /> Edit
              </Button>
            )}
            {isEstimate && !["Paid", "Converted"].includes(doc.status) && (
              <Button variant="outline" onClick={onCopyAcceptLink} disabled={busy} data-testid="detail-copy-accept-link" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] border-blue-200 text-blue-700 hover:bg-blue-50">
                <Link2 className="h-4 w-4 mr-1.5" /> Copy accept link
              </Button>
            )}
            {isEstimate && ["Sent", "Accepted"].includes(doc.status) && (
              <Button onClick={onPresent} disabled={busy} data-testid="detail-present-to-customer" className="w-full md:w-auto h-12 md:h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] md:text-[13px] font-semibold">
                <PenLine className="h-4 w-4 mr-1.5" /> Present
              </Button>
            )}
            {isEstimate && doc.status === "Accepted" && !doc.convertedInvoiceId && (
              <Button onClick={onConvert} disabled={busy} data-testid="convert-to-invoice" className="w-full md:w-auto h-12 md:h-9 bg-violet-600 hover:bg-violet-700 text-white text-[14px] md:text-[13px] font-semibold">
                <ArrowRightLeft className="h-4 w-4 mr-1.5" /> Convert to Invoice
              </Button>
            )}
            {!isEstimate && Number(doc.balanceDue) > 0 && doc.status !== "Voided" && (
              <Button variant="outline" onClick={onCopyPayLink} disabled={busy} data-testid="detail-copy-pay-link" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <Link2 className="h-4 w-4 mr-1.5" /> Copy pay link
              </Button>
            )}
            {user?.role === "admin" && (
              <Button variant="outline" onClick={onDelete} disabled={busy} data-testid="detail-delete" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] text-rose-600 hover:bg-rose-50 hover:border-rose-300 border-slate-200">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            )}
          </>
        )}
        {/* Accept Payment — always for unpaid invoices; for converted estimates
            it records the payment on the linked invoice. Gated by role, NOT
            canEdit (a Converted estimate is edit-locked but still lets us
            take money on its follow-up invoice). */}
        {isAdmin && !isEstimate && Number(doc.balanceDue) > 0 && doc.status !== "Voided" && (
          <Button
            onClick={() => setAcceptTarget(doc)}
            disabled={busy}
            data-testid="detail-accept-payment"
            className="w-full md:w-auto h-12 md:h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] md:text-[13px] font-semibold"
          >
            <DollarSign className="h-4 w-4 mr-1.5" /> Accept Payment
          </Button>
        )}
        {isAdmin && isEstimate && doc.convertedInvoiceId && (linkedInvoiceBalance === null || linkedInvoiceBalance > 0) && (
          <Button
            onClick={openAcceptPayment}
            disabled={busy}
            data-testid="detail-accept-payment"
            className="w-full md:w-auto h-12 md:h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] md:text-[13px] font-semibold"
          >
            <DollarSign className="h-4 w-4 mr-1.5" /> Accept Payment
          </Button>
        )}
        {isEstimate && doc.convertedInvoiceId && (
          <Button variant="outline" onClick={onViewInvoice} disabled={busy} data-testid="view-invoice" className="w-full md:w-auto h-12 md:h-9 text-[14px] md:text-[13px] border-slate-200">
            <ArrowRightLeft className="h-4 w-4 mr-1.5" /> View Invoice
          </Button>
        )}
        {!isEstimate && doc.status === "Paid" && onCloseJob && (
          <Button
            onClick={onCloseJob}
            disabled={busy}
            data-testid="detail-close-the-job"
            className="w-full md:w-auto h-12 md:h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] md:text-[13px] font-semibold"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Close the Job
          </Button>
        )}
      </div>

      {acceptTarget && (
        <ProcessPaymentModal
          kind="invoice"
          docId={acceptTarget.id}
          docLabel={`Invoice ${acceptTarget.id}`}
          customerName={acceptTarget.customer}
          balance={Number(acceptTarget.balanceDue || 0)}
          onClose={() => setAcceptTarget(null)}
          onDone={(updatedInv) => {
            setAcceptTarget(null);
            // For the invoice view we can update in place; for estimates we
            // push the parent so the linked-invoice cache stays in sync.
            if (onUpdated) onUpdated(isEstimate ? doc : updatedInv);
          }}
        />
      )}

      {refundTarget && (
        <RefundDialog
          payment={refundTarget}
          invoiceId={doc.id}
          onClose={() => setRefundTarget(null)}
          onDone={(updated) => {
            setRefundTarget(null);
            if (onUpdated) onUpdated(updated);
          }}
        />
      )}
    </>
  );
}


function SellMembershipDialog({ plans, onClose, onPick }) {
  const [planId, setPlanId] = useState(plans[0]?.id || "");
  const plan = plans.find((p) => p.id === planId);
  const terms = plan ? MEMBERSHIP_TERMS.filter((t) => plan.termPrices?.[t] > 0) : [];
  const [term, setTerm] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTerm(terms[0] || ""); }, [planId]);

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="sell-membership-dialog">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900 flex items-center gap-2"><Crown className="h-4 w-4 text-violet-600" /> Sell a Membership</div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Plan</label>
            <div className="grid grid-cols-3 gap-2">
              {plans.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlanId(p.id)}
                  data-testid={`sell-plan-${p.id}`}
                  className={`rounded-lg border-2 px-2 py-2 text-center transition-all ${planId === p.id ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <div className="font-bold text-[13px] text-slate-800">{p.name}</div>
                  <div className="text-[11px] font-semibold" style={{ color: p.color }}>{p.discountPct}% off</div>
                </button>
              ))}
            </div>
          </div>
          {plan && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Term</label>
              {terms.length === 0 ? (
                <div className="text-[12.5px] text-slate-400 italic">No term prices set for this plan.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {terms.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      data-testid={`sell-term-${t}`}
                      className={`rounded-lg border-2 px-3 py-2 flex items-center justify-between transition-all ${term === t ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <span className="text-[12.5px] text-slate-700">{termLabel(t)}</span>
                      <span className="text-[13px] font-bold text-slate-900">{money(plan.termPrices[t])}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
          <Button
            onClick={() => plan && term && onPick(plan, term)}
            disabled={!plan || !term}
            data-testid="sell-membership-confirm"
            className="h-9 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-semibold"
          >
            Add · {plan && term ? money(plan.termPrices[term]) : "—"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PricebookPicker({ onClose, onPick, plans = [], onPickMembership }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("service"); // service | material
  const [sel, setSel] = useState({}); // { [id]: qty }

  useEffect(() => {
    pricebookApi
      .list()
      .then((list) => setItems(list.filter((i) => i.isActive !== false)))
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load pricebook");
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (i.kind !== kindFilter) return false;
      if (!q) return true;
      return `${i.id} ${i.name} ${i.sku || ""} ${i.category || ""} ${i.description || ""}`.toLowerCase().includes(q);
    });
  }, [items, search, kindFilter]);

  const setQty = (id, qty) =>
    setSel((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });

  const selectedIds = Object.keys(sel);
  const selectedCount = selectedIds.length;
  const selectedTotal = items.reduce((sum, i) => sum + (sel[i.id] ? Number(i.unitPrice || 0) * sel[i.id] : 0), 0);

  const addAll = () => {
    const picks = items.filter((i) => sel[i.id]).map((i) => ({ item: i, qty: sel[i.id] }));
    if (!picks.length) return;
    onPick(picks);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pricebook-picker"
        className="bg-white rounded-t-3xl shadow-2xl w-full max-w-[480px] flex flex-col overflow-hidden"
        style={{ maxHeight: "88vh" }}
      >
        <div className="shrink-0 pt-2.5 flex justify-center">
          <span className="h-1.5 w-10 rounded-full bg-slate-600" />
        </div>
        <div className="shrink-0 px-5 pt-3 pb-2 flex items-center justify-between">
          <div className="text-[22px] font-black text-[#0B1E3F]">Add from Pricebook</div>
          <button onClick={onClose} data-testid="picker-close" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        <div className="shrink-0 px-5 pb-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items..."
              data-testid="picker-search"
              className="w-full h-12 pl-11 pr-4 rounded-full bg-slate-50 border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
        </div>

        <div className="shrink-0 px-5 pb-3">
          <div className="grid grid-cols-3 gap-2 bg-slate-100 rounded-full p-1">
            {[
              { k: "service", l: "Services" },
              { k: "material", l: "Materials" },
              { k: "membership", l: "Memberships" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setKindFilter(t.k)}
                data-testid={`picker-filter-${t.k}`}
                className={`h-11 rounded-full text-[12px] font-bold transition-all ${
                  kindFilter === t.k ? "bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white shadow" : "text-slate-300"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-2 space-y-3" data-testid="picker-list">
          {kindFilter === "membership" ? (
            plans.length === 0 ? (
              <div className="py-10 text-center text-[13px] text-slate-400 italic">No membership plans yet.</div>
            ) : (
              plans.map((p) => {
                const price = Number(p.termPrices?.["1yr"]) || 0;
                return (
                  <div key={p.id} data-testid={`picker-membership-${p.id}`} className="rounded-2xl p-4 border bg-slate-50 border-slate-700/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Crown className="h-4 w-4 shrink-0" style={{ color: p.color || "#7C3AED" }} />
                          <div className="text-[17px] font-bold text-[#0B1E3F] leading-tight">{p.name}</div>
                        </div>
                        <span className="inline-block mt-2 px-3 py-1 rounded-full text-[12px] font-semibold bg-violet-100 text-violet-700">
                          {p.discountPct}% member discount · 1 Year
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="text-[18px] font-black text-[#0B1E3F]">{price > 0 ? money(price) : "—"}</div>
                        <button
                          onClick={() => onPickMembership && onPickMembership(p)}
                          data-testid={`picker-membership-add-${p.id}`}
                          className="h-9 px-4 rounded-full bg-[#2563EB] text-white text-[13px] font-bold flex items-center justify-center active:scale-95 transition-transform"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )
          ) : loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-slate-400 italic">No items match your search.</div>
          ) : (
            filtered.map((i) => {
              const qty = sel[i.id] || 0;
              const isSel = qty > 0;
              return (
                <div
                  key={i.id}
                  data-testid={`picker-item-${i.id}`}
                  className={`rounded-2xl p-4 border transition-all ${
                    isSel
                      ? "bg-[#0F1E3C] border-[#3B82F6] shadow-[0_0_18px_-4px_rgba(37,99,235,0.6)]"
                      : "bg-slate-50 border-slate-700/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[17px] font-bold text-[#0B1E3F] leading-tight">{i.name}</div>
                      {i.category && (
                        <span className="inline-block mt-2 px-3 py-1 rounded-full text-[12px] font-semibold bg-slate-100 text-slate-300">
                          {i.category}
                        </span>
                      )}
                    </div>
                    <div className="text-[18px] font-black text-[#0B1E3F] shrink-0">{money(i.unitPrice)}</div>
                  </div>
                  <div className="flex justify-end mt-2">
                    {isSel ? (
                      <div className="inline-flex items-center bg-slate-100 rounded-full">
                        <button
                          onClick={() => setQty(i.id, qty - 1)}
                          data-testid={`picker-qty-dec-${i.id}`}
                          className="h-9 w-10 flex items-center justify-center text-slate-200 active:bg-slate-200 rounded-l-full"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center text-[15px] font-bold text-[#0B1E3F]">{qty}</span>
                        <button
                          onClick={() => setQty(i.id, qty + 1)}
                          data-testid={`picker-qty-inc-${i.id}`}
                          className="h-9 w-10 flex items-center justify-center text-blue-400 active:bg-slate-200 rounded-r-full"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setQty(i.id, 1)}
                        data-testid={`picker-add-${i.id}`}
                        className="h-9 w-9 rounded-full bg-[#2563EB] text-white flex items-center justify-center active:scale-95 transition-transform"
                      >
                        <Plus className="h-5 w-5" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {kindFilter !== "membership" && (
        <div
          className="shrink-0 px-5 py-4 border-t border-slate-700/40 flex items-center justify-between gap-3 bg-white"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <div className="text-[14px] text-slate-300 min-w-0">
            Selected: <span className="font-bold text-[#0B1E3F]">{selectedCount} item{selectedCount === 1 ? "" : "s"}</span>
            {" – "}
            <span className="font-bold text-[#0B1E3F]">{money(selectedTotal)}</span>
          </div>
          <button
            onClick={addAll}
            disabled={selectedCount === 0}
            data-testid="picker-add-to-estimate"
            className="shrink-0 h-12 px-6 rounded-xl bg-[#2563EB] text-white text-[15px] font-bold active:scale-95 transition-transform disabled:opacity-40"
          >
            Add to Estimate
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

function PaymentsSection({ invoice, onUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("check");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);

  const addPayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Amount must be positive");
      return;
    }
    setBusy(true);
    try {
      const updated = await invoicesApi.addPayment(invoice.id, {
        amount: amt,
        method,
        reference,
        note,
      });
      onUpdated(updated);
      setAmount(""); setReference(""); setNote("");
      toast.success(`Payment recorded · balance ${money(updated.balanceDue)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (payId) => {
    if (!window.confirm("Reverse this payment?")) return;
    setBusy(true);
    try {
      const updated = await invoicesApi.removePayment(invoice.id, payId);
      onUpdated(updated);
      toast.success("Payment reversed");
    } catch (err) {
      toast.error("Reverse failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const canRefund = (p) => {
    if (!isAdmin) return false;
    if ((p.amount || 0) <= 0) return false; // negative rows are already refunds
    const method = (p.method || "").toLowerCase();
    if (!["card", "ach"].includes(method)) return false;
    if (!p.ledgerPaymentId) return false;
    const refunded = Number(p.refundedTotal || 0);
    return refunded + 0.005 < Number(p.amount || 0);
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Payments</div>
      <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
        {/* Existing payments */}
        {(invoice.payments || []).length === 0 ? (
          <div className="text-[12px] text-slate-400 italic">No payments recorded yet.</div>
        ) : (
          <div className="space-y-1">
            {invoice.payments.map((p) => {
              const isRefundRow = (p.amount || 0) < 0 || p.method === "refund";
              const refundable = canRefund(p);
              const refunded = Number(p.refundedTotal || 0);
              return (
                <div key={p.id} data-testid={`payment-row-${p.id}`} className={`flex items-center justify-between text-[12.5px] py-1 px-2 rounded ${isRefundRow ? "bg-rose-50/60" : "bg-slate-50/60"}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <DollarSign className={`h-3.5 w-3.5 shrink-0 ${isRefundRow ? "text-rose-600" : "text-emerald-600"}`} />
                    <span className={`font-semibold ${isRefundRow ? "text-rose-700" : "text-slate-800"}`}>{money(p.amount)}</span>
                    <span className="text-slate-500 uppercase text-[10.5px] font-semibold">{p.method}</span>
                    {refunded > 0 && !isRefundRow && (
                      <span
                        className="text-[10.5px] font-semibold text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded"
                        data-testid={`payment-refunded-${p.id}`}
                      >
                        {p.status === "refunded" ? "Fully refunded" : `−${money(refunded)} refunded`}
                      </span>
                    )}
                    {p.reference && <span className="text-slate-400 text-[11px]">· {p.reference}</span>}
                    <span className="text-slate-400 text-[11px]">· {formatStamp(p.receivedAt)}</span>
                    <span className="text-slate-400 text-[11px] truncate">· {p.recordedBy}</span>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {refundable && (
                      <button
                        onClick={() => setRefundTarget(p)}
                        disabled={busy}
                        data-testid={`payment-refund-${p.id}`}
                        className="h-6 px-2 rounded text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50 inline-flex items-center gap-1"
                        title="Refund via processor"
                      >
                        <Undo2 className="h-3 w-3" /> Refund
                      </button>
                    )}
                    <button onClick={() => removePayment(p.id)} disabled={busy} data-testid={`payment-remove-${p.id}`} className="h-6 w-6 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600 disabled:opacity-50" title="Reverse (manual undo)">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add payment form */}
        {invoice.balanceDue > 0 && invoice.status !== "Voided" && (
          <div className="pt-2 border-t border-slate-100">
            <div className="grid grid-cols-12 gap-1.5">
              <input
                type="number"
                min={0}
                step={0.01}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Amount"
                data-testid="payment-amount"
                className={`${inputClass} col-span-3`}
              />
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method" className={`${inputClass} col-span-2`}>
                {PAY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Ref / Check #"
                data-testid="payment-ref"
                className={`${inputClass} col-span-3`}
              />
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional)"
                data-testid="payment-note"
                className={`${inputClass} col-span-2`}
              />
              <Button
                onClick={addPayment}
                disabled={busy || !amount}
                data-testid="payment-add"
                className="h-9 col-span-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12.5px] font-semibold"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Record</>}
              </Button>
            </div>
            <button
              onClick={() => setAmount(String(invoice.balanceDue))}
              data-testid="payment-fill-balance"
              className="mt-1.5 text-[11px] text-blue-600 hover:text-blue-700 font-medium"
            >
              Fill with balance due ({money(invoice.balanceDue)})
            </button>
          </div>
        )}
      </div>

      {refundTarget && (
        <RefundDialog
          payment={refundTarget}
          invoiceId={invoice.id}
          onClose={() => setRefundTarget(null)}
          onDone={(updated) => {
            setRefundTarget(null);
            onUpdated(updated);
          }}
        />
      )}
    </div>
  );
}

function RefundDialog({ payment, invoiceId, onClose, onDone }) {  const remaining = Number(payment.amount || 0) - Number(payment.refundedTotal || 0);
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) { toast.error("Enter a positive amount"); return; }
    if (n > remaining + 0.005) { toast.error(`Cannot exceed remaining ${money(remaining)}`); return; }
    if (!window.confirm(`Refund ${money(n)} to the customer via ${payment.provider || "the processor"}?`)) return;
    setBusy(true);
    try {
      const updated = await invoicesApi.refundPayment(invoiceId, payment.id, {
        amount: n,
        reason: reason.trim() || undefined,
      });
      toast.success(`Refunded ${money(n)}`);
      onDone(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center px-4" onClick={onClose} data-testid="refund-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
              <Undo2 className="h-4 w-4 text-rose-600" />
              Refund payment
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              via {payment.provider || "processor"} · original {money(payment.amount)}
              {Number(payment.refundedTotal || 0) > 0 && ` · already refunded ${money(payment.refundedTotal)}`}
            </div>
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Amount</label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
              <input
                type="number"
                min={0.5}
                max={remaining}
                step={0.01}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="refund-amount"
                className="w-full h-10 pl-6 pr-3 rounded-md border border-slate-200 bg-white text-[14px] font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
              />
            </div>
            <button
              type="button"
              onClick={() => setAmount(remaining.toFixed(2))}
              data-testid="refund-fill-remaining"
              className="mt-1 text-[11px] text-blue-600 hover:text-blue-700 font-medium"
            >
              Fill remaining ({money(remaining)})
            </button>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Reason (optional)</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="refund-reason"
              className="w-full h-10 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
            >
              <option value="">Choose a reason…</option>
              <option value="Requested by customer">Requested by customer</option>
              <option value="Duplicate charge">Duplicate charge</option>
              <option value="Service not delivered">Service not delivered</option>
              <option value="Overcharged">Overcharged</option>
              <option value="Goodwill">Goodwill</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="flex items-center gap-2 pt-1 justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={busy}
              data-testid="refund-cancel"
              className="h-9 text-[13px]"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="refund-submit"
              className="h-9 bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold px-4"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5 mr-1.5" />}
              Refund {money(parseFloat(amount) || 0)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full h-10 md:h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[15px] md:text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] md:text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
